import { resourcesCopy } from '@/copy/resources'
import { SelectField, TextareaField, TextField } from '@/ui/Field/Field'
import { Text } from '@/ui/layout/Text'
import { PredicateTermRows } from './PredicateTermRows'
import {
  COMPARISONS,
  type Comparison,
  isList,
  parseRows,
  splitList,
} from './predicate'
import type { FieldProps } from './shared'

interface PredicateFieldsProps extends Pick<FieldProps, 'fields' | 'set'> {
  /** Input id prefix (`route`, `workflow`). */
  idPrefix: string
}

/**
 * The shared predicate editor for routes and workflows: the top-level
 * `when` + comparison, and extra `all` conditions (crates/tools
 * `handlers/predicate.rs`).
 */
export function PredicateFields({
  fields,
  set,
  idPrefix,
}: PredicateFieldsProps) {
  const copy = resourcesCopy.forms.predicate
  const op = (fields.op || 'equals') as Comparison
  const allRows = parseRows(fields.all)
  const value = fields.value ?? ''
  const preserved = fields.preserved
    ? Object.keys(JSON.parse(fields.preserved) as object)
    : []
  const changeOp = (next: string) => {
    // A newline-separated `in` list doesn't fit a single-line input.
    if (isList(op) && !isList(next as Comparison))
      set('value', splitList(value).join(', '))
    set('op', next)
  }

  return (
    <>
      <TextField
        id={`${idPrefix}-when`}
        label={copy.when}
        help={copy.whenHelp}
        value={fields.when ?? '{event_type}'}
        onValue={(v) => set('when', v)}
        inputProps={{ placeholder: copy.whenPlaceholder }}
      />
      <SelectField
        id={`${idPrefix}-op`}
        label={copy.comparison}
        value={op}
        onValue={changeOp}
      >
        {COMPARISONS.map((k) => (
          <option key={k} value={k}>
            {copy.comparisons[k]}
          </option>
        ))}
      </SelectField>
      {isList(op) ? (
        <TextareaField
          id={`${idPrefix}-value`}
          label={copy.values}
          help={copy.valueHelp.in}
          value={value}
          onValue={(v) => set('value', v)}
          textareaProps={{ placeholder: copy.valuesPlaceholder, rows: 3 }}
        />
      ) : (
        <TextField
          id={`${idPrefix}-value`}
          label={copy.value}
          help={copy.valueHelp[op]}
          value={value}
          onValue={(v) => set('value', v)}
          inputProps={{ placeholder: copy.valuePlaceholder }}
        />
      )}
      <PredicateTermRows
        label={copy.andAlso}
        help={copy.andAlsoHelp}
        rows={allRows}
        onRows={(rows) =>
          set('all', rows.length > 0 ? JSON.stringify(rows) : '')
        }
      />
      {preserved.length > 0 && (
        <Text as="p" variant="caption" tone="text-mute">
          {copy.kept(preserved.join(', '))}
        </Text>
      )}
    </>
  )
}
